import {
  IActionType,
  VARIABLE_TEXT_FEATURE_FLAG_TEXT,
  VariableSelectFeatureFlag,
  VariableTextFeatureFlag,
} from '@makyoapp/makyo-common'
import { ColumnTypes } from '@makyoapp/makyo-tw-ui'

import { ALLOWED_PROMPT_TYPES } from '../constants'
import {
  BuilderComponentGroup,
  BuilderComponentSettingType,
  ComponentSettingKey,
  MdShape,
  SpacingInputUnit,
} from '../types'
import { LABELS } from '../types/labels'
import {
  hoverEffectOptions,
  makeClampWidthItems,
  makeDataSource,
  makeFilter,
  makeSorting,
  makeTextColorSettings,
  makeTypographyGroup,
} from '../utils'

export const UtilityComponents: BuilderComponentGroup = {
  id: 'app',
  title: 'Utilities',
  components: [
    // login form
    {
      compId: 'login-form',
      title: 'Login Form',
      searchKeywords: [
        'login',
        'authentication',
        'sign in',
        'login form',
        'credentials',
        'password field',
        'user login',
      ],
      icon: 'Login16',
      nested: false,
      isForm: false,
      settings: {
        designGroup: {
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LoginFormDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LoginFormDesignButtonColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BUTTON_KEY,
                defaultValue: 'primary-20',
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LoginFormDesignFieldShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.FIELD_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.LoginFormDesignButtonShape,
                type: BuilderComponentSettingType.Select,
                label: LABELS.BUTTON_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.LoginFormDesignWidth).items,
              {
                id: ComponentSettingKey.LoginFormDesignButtonSize,
                type: BuilderComponentSettingType.Select,
                label: LABELS.BUTTON_SIZE_KEY,
                defaultValue: 'lg',
                selectSettings: {
                  options: Object.entries({
                    Small: 'sm',
                    Medium: 'md',
                    Large: 'lg',
                    Icon: 'icon',
                  }).map(([label, value]) => ({
                    label,
                    value,
                  })),
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LoginFormDesignButtonPadding,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.BUTTON_PADDING_KEY,
                // defaultValue: {}, // see login-form.design.ts
              },
              {
                id: ComponentSettingKey.LoginFormDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
                // defaultValue: {}, // see login-form.design.ts
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.LoginFormDesignTypography, [
            {
              id: ComponentSettingKey.LoginFormDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Medium',
            },
          ]),
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.LoginFormDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.LoginFormDesignTitleColor,
                      ComponentSettingKey.LoginFormDesignTitleColorSolid,
                      ComponentSettingKey.LoginFormDesignTitleColorStartGradient,
                      ComponentSettingKey.LoginFormDesignTitleColorEndGradient,
                      ComponentSettingKey.LoginFormDesignTitleColorGradientDirection,
                      ComponentSettingKey.LoginFormDesignScheme,
                      LABELS.TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.LABEL_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.LoginFormDesignLabelColor,
                      ComponentSettingKey.LoginFormDesignLabelColorSolid,
                      ComponentSettingKey.LoginFormDesignLabelColorStartGradient,
                      ComponentSettingKey.LoginFormDesignLabelColorEndGradient,
                      ComponentSettingKey.LoginFormDesignLabelColorGradientDirection,
                      ComponentSettingKey.LoginFormDesignScheme,
                      LABELS.LABEL_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.LoginFormActionOnLogin,
            label: LABELS.ON_LOGIN_KEY,
            isSimple: false,
            defaultValue: [],
            actionTypes: [],
          },
          {
            id: ComponentSettingKey.LoginFormActionOnLogout,
            label: LABELS.ON_LOGOUT_KEY,
            isSimple: false,
            defaultValue: [],
            actionTypes: [],
          },
        ],
        behavior: {
          visibility: true,
        },
      },
    },

    // vertical menu
    {
      compId: 'vertical-menu',
      title: 'Menu',
      searchKeywords: ['sidebar', 'vertical nav', 'navigation menu', 'side menu', 'navigation drawer', 'menu'],
      icon: 'Menu16',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDataSource,
                type: BuilderComponentSettingType.MenuSelect,
                label: LABELS.DATA_SOURCE_KEY,
                menuSelectSettings: {
                  autoDefault: true,
                },
              },
            ],
          },
        },
        designGroup: {
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.VerticalMenuDesignTypography, [
            {
              id: ComponentSettingKey.VerticalMenuDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.VerticalMenuDesignItemsTypography,
              label: 'Item Menu',
              defaultValue: 'Body Medium',
            },
          ]),
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDesignCompMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.VerticalMenuDesignTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.TITLE_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.VerticalMenuDesignItemsMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.ITEMS_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDesignAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.VerticalMenuDesignItemsAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Items Alignment',
                defaultValue: 'start',
              },
              {
                id: ComponentSettingKey.VerticalMenuDesignOverflowMenuDropdownAlign,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Dropdown Align',
                defaultValue: 'end',
              },
            ],
          },
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.VerticalMenuDesignTitleColor,
                      ComponentSettingKey.VerticalMenuDesignTitleColorSolid,
                      ComponentSettingKey.VerticalMenuDesignTitleColorStartGradient,
                      ComponentSettingKey.VerticalMenuDesignTitleColorEndGradient,
                      ComponentSettingKey.VerticalMenuDesignTitleColorGradientDirection,
                      ComponentSettingKey.VerticalMenuDesignScheme,
                      LABELS.TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.ITEMS_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.VerticalMenuDesignItemsColor,
                      ComponentSettingKey.VerticalMenuDesignItemsColorSolid,
                      ComponentSettingKey.VerticalMenuDesignItemsColorStartGradient,
                      ComponentSettingKey.VerticalMenuDesignItemsColorEndGradient,
                      ComponentSettingKey.VerticalMenuDesignItemsColorGradientDirection,
                      ComponentSettingKey.VerticalMenuDesignScheme,
                      LABELS.ITEMS_KEY,
                      'adaptive'
                    ).items,
                  ],
                  'Overflow Menu': [
                    {
                      id: ComponentSettingKey.VerticalMenuDesignOverflowMenuBackgroundColor,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Background Color',
                      defaultValue: 'primary',
                      colorVariantSettings: {
                        schemeId: ComponentSettingKey.VerticalMenuDesignScheme,
                        fullColor: 'bg',
                      },
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDesignOrientation,
                type: BuilderComponentSettingType.IconSelect,
                label: LABELS.ORIENTATION_KEY,
                defaultValue: 'horizontal',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: LABELS.HORIZONTAL_KEY,
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: LABELS.VERTICAL_KEY,
                    },
                  ],
                },
              },
            ],
          },
          'Overflow Menu': {
            items: [
              {
                id: ComponentSettingKey.VerticalMenuDataMaxVisibleItems,
                type: BuilderComponentSettingType.Number,
                label: 'Max Visible Items',
                defaultValue: undefined,
                numberSettings: {
                  min: 0,
                  max: 100,
                  step: 1,
                },
              },
              {
                id: ComponentSettingKey.VerticalMenuDataOverflowIcon,
                type: BuilderComponentSettingType.Icon,
                label: 'Overflow Icon',
                defaultValue: 'menu',
              },
            ],
          },
        },
        behavior: {
          visibility: true,
          custom: {
            [LABELS.SHOW_HIDE_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.VerticalMenuBehaviorShowIcon,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.SHOW_ICON_KEY,
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.VerticalMenuBehaviorShowTitle,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.SHOW_TITLE_KEY,
                  defaultValue: true,
                },
              ],
            },
          },
        },
      },
    },

    // calendar
    {
      compId: 'calendar',
      title: 'Calendar',
      searchKeywords: ['calendar', 'schedule', 'events', 'booking calendar', 'planner', 'date view', 'agenda'],
      icon: 'Calendar16',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.CalendarDataSource),
              {
                id: ComponentSettingKey.CalendarDataTitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.TITLE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CalendarDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.RichText,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Boolean,
                    ColumnTypes.Coordinate,
                    ColumnTypes.DateTime,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CalendarDataStartDate,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.START_DATE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CalendarDataSource,
                  columnTypes: [ColumnTypes.DateTime, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CalendarDataEndDate,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.END_DATE_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CalendarDataSource,
                  columnTypes: [ColumnTypes.DateTime, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
              {
                id: ComponentSettingKey.CalendarDataColor,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.COLOR_KEY,
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.CalendarDataSource,
                  columnTypes: [ColumnTypes.Color, ColumnTypes.Lookup],
                  includeLookupType: true,
                },
              },
            ],
          },
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.CalendarDataFilter,
            ComponentSettingKey.CalendarDataSource
          ),
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.CalendarDataSortBy,
            ComponentSettingKey.CalendarDataSortType,
            ComponentSettingKey.CalendarDataSource
          ),
        },
        designGroup: {
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDataSetLimit,
                label: 'Dataset Limit',
                type: BuilderComponentSettingType.DataSetLimit,
                defaultValue: {
                  enabled: false,
                  limit: 100,
                  value: 10,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDesignCompHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.CalendarDesignTypography, [
            {
              id: ComponentSettingKey.CalendarDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Small',
            },
            {
              id: ComponentSettingKey.CalendarDesignContentTypography,
              label: LABELS.CONTENT_KEY,
              defaultValue: 'Body Small',
            },
          ]),
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDesignBackgroundColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BACKGROUND_COLOR_KEY,
                defaultValue: 'primary-95',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CalendarDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.CalendarDesignFilterBgColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_BG_KEY,
                defaultValue: 'primary-95',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CalendarDesignScheme,
                },
              },
              {
                id: ComponentSettingKey.CalendarDesignFilterChipColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.FILTERS_CHIP_KEY,
                defaultValue: 'primary-95',
                colorVariantSettings: {
                  fullColor: 'bg',
                  schemeId: ComponentSettingKey.CalendarDesignScheme,
                },
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDesignCornerRadius,
                type: BuilderComponentSettingType.Select,
                label: LABELS.CORNER_RADIUS_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.CalendarDesignFilterCornerRadius,
                label: LABELS.FILTERS_CHIP_KEY,
                type: BuilderComponentSettingType.Select,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [...makeClampWidthItems(ComponentSettingKey.CalendarDesignWidth).items],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.CalendarDesignPadding,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.PADDING_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.CalendarDesignFilterMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.FILTERS_KEY,
              },
            ],
          },
          [LABELS.ELEVATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.CalendarDesignElevation,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.ELEVATION_KEY,
                defaultValue: true,
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.CalendarActionOnClick,
            label: LABELS.ON_CLICK_EVENT_KEY,
            isSimple: false,
            defaultValue: [],
            actionTypes: [IActionType.ShowDetailsScreen],
          },
          {
            id: ComponentSettingKey.CalendarActionAddEvent,
            label: LABELS.ADD_EVENT_KEY,
            isSimple: false,
            defaultValue: [],
            actionTypes: [],
          },
        ],
        behavior: {
          visibility: true,
          custom: {
            [LABELS.APP_FILTERS_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CalendarBehaviorUserFilter,
                  label: '',
                  type: BuilderComponentSettingType.UserColumnFilter,
                  columnFilterSettings: {
                    tableSourceId: ComponentSettingKey.CalendarDataSource,
                  },
                },
              ],
            },
            [LABELS.DEFAULT_VIEW_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CalendarBehaviorDefaultView,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.DEFAULT_VIEW_KEY,
                  defaultValue: 'month',
                  selectSettings: {
                    options: [
                      {
                        value: 'month',
                        label: 'Month',
                      },
                      {
                        value: 'week',
                        label: 'Week',
                      },
                      {
                        value: 'day',
                        label: 'Day',
                      },
                    ],
                  },
                },
              ],
            },
            [LABELS.AUTO_FIT_DAY_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.CalendarBehaviorAutoFitDay,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.AUTO_FIT_DAY_KEY,
                  defaultValue: true,
                },
              ],
            },
          },
        },
      },
    },

    // prompt
    {
      compId: 'prompt',
      title: 'Prompt',
      searchKeywords: ['prompt', 'input box', 'command line', 'terminal', 'text prompt', 'query', 'console'],
      icon: 'Prompt16',
      nested: false,
      settings: {
        dataGroup: {
          'Default Prompts': {
            items: [
              makeDataSource(ComponentSettingKey.PromptDataSource),
              {
                id: ComponentSettingKey.PromptDataTitle,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Title Column',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PromptDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                },
              },
              {
                id: ComponentSettingKey.PromptDataDescription,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Description Column',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PromptDataSource,
                  columnTypes: [
                    ColumnTypes.Text,
                    ColumnTypes.Function,
                    ColumnTypes.URL,
                    ColumnTypes.Email,
                    ColumnTypes.Address,
                    ColumnTypes.Join,
                    ColumnTypes.Color,
                    ColumnTypes.Number,
                    ColumnTypes.Phone,
                    ColumnTypes.RowID,
                    ColumnTypes.Lookup,
                  ],
                },
              },
            ],
          },
          [LABELS.PLACEHOLDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDataPlaceholder,
                type: BuilderComponentSettingType.Text,
                label: LABELS.PLACEHOLDER_KEY,
              },
            ],
          },
          [LABELS.SORTING_KEY]: makeSorting(
            ComponentSettingKey.PromptDataSortBy,
            ComponentSettingKey.PromptDataSortType,
            ComponentSettingKey.PromptDataSource
          ),
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.PromptDataFilter,
            ComponentSettingKey.PromptDataSource
          ),
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.BACKGROUND_COLOR_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.PromptDesignBackgroundColor,
                      ComponentSettingKey.PromptDesignBackgroundColorSolid,
                      ComponentSettingKey.PromptDesignBackgroundColorStartGradient,
                      ComponentSettingKey.PromptDesignBackgroundColorEndGradient,
                      ComponentSettingKey.PromptDesignBackgroundColorGradientDirection,
                      ComponentSettingKey.PromptDesignScheme,
                      LABELS.BACKGROUND_COLOR_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.BUTTON_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.PromptDesignButtonColorVariant,
                      ComponentSettingKey.PromptDesignButtonColorVariantSolid,
                      ComponentSettingKey.PromptDesignButtonColorVariantStartGradient,
                      ComponentSettingKey.PromptDesignButtonColorVariantEndGradient,
                      ComponentSettingKey.PromptDesignButtonColorVariantGradientDirection,
                      ComponentSettingKey.PromptDesignScheme,
                      LABELS.BUTTON_KEY,
                      'adaptive'
                    ).items,
                  ],
                  // [LABELS.DIVIDER_KEY]: [
                  //   ...makeTextColorSettings(
                  //     ComponentSettingKey.PromptDesignDividerColor,
                  //     ComponentSettingKey.PromptDesignDividerColorSolid,
                  //     ComponentSettingKey.PromptDesignDividerColorStartGradient,
                  //     ComponentSettingKey.PromptDesignDividerColorEndGradient,
                  //     ComponentSettingKey.PromptDesignDividerColorGradientDirection,
                  //     ComponentSettingKey.PromptDesignScheme,
                  //     LABELS.DIVIDER_KEY,
                  //     'adaptive'
                  //   ).items,
                  // ],
                  [LABELS.PLACEHOLDER_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.PromptDesignPlaceholderColor,
                      ComponentSettingKey.PromptDesignPlaceholderColorSolid,
                      ComponentSettingKey.PromptDesignPlaceholderColorStartGradient,
                      ComponentSettingKey.PromptDesignPlaceholderColorEndGradient,
                      ComponentSettingKey.PromptDesignPlaceholderColorGradientDirection,
                      ComponentSettingKey.PromptDesignScheme,
                      LABELS.PLACEHOLDER_KEY,
                      'adaptive'
                    ).items,
                  ],
                  'Prompts Chips': [
                    ...makeTextColorSettings(
                      ComponentSettingKey.PromptDesignChipColor,
                      ComponentSettingKey.PromptDesignChipColorSolid,
                      ComponentSettingKey.PromptDesignChipColorStartGradient,
                      ComponentSettingKey.PromptDesignChipColorEndGradient,
                      ComponentSettingKey.PromptDesignChipColorGradientDirection,
                      ComponentSettingKey.PromptDesignScheme,
                      LABELS.PLACEHOLDER_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
              // {
              //   id: ComponentSettingKey.PromptDesignBackgroundColor,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.BACKGROUND_COLOR_KEY,
              //   defaultValue: 'primary-10',
              //   colorVariantSettings: {
              //     fullColor: 'bg',
              //     schemeId: ComponentSettingKey.PromptDesignScheme,
              //   },
              // },
              // {
              //   id: ComponentSettingKey.PromptDesignButtonColorVariant,
              //   type: BuilderComponentSettingType.ColorVariant,
              //   label: LABELS.BUTTON_KEY,
              //   defaultValue: 'primary-20',
              //   colorVariantSettings: {
              //     fullColor: 'bg',
              //     schemeId: ComponentSettingKey.PromptDesignScheme,
              //   },
              // },
            ],
          },
          [LABELS.SIZING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignWidth,
                type: BuilderComponentSettingType.Slider,
                label: LABELS.WIDTH_KEY,
                defaultValue: [100],
                clampInAllDevices: true,
                sliderSettings: {
                  max: 3890,
                  min: 10,
                  step: 1,
                  range: false,
                  unit: '%',
                  showUnit: true,
                },
              },
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignHAlign,
                type: BuilderComponentSettingType.ComponentAlignment,
                defaultValue: 'parent',
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.PromptDesignChipsAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Prompts Chips',
                defaultValue: 'center',
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_MARGIN_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.PromptDesignChipsGap,
                type: BuilderComponentSettingType.Slider,
                label: 'Prompts Chips Gap',
                defaultValue: [8],
                sliderSettings: {
                  min: 0,
                  max: 100,
                  step: 1,
                  unit: 'px',
                },
              },
              {
                id: ComponentSettingKey.PromptDesignSuggestionPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Suggestion Padding',
                defaultValue: {
                  top: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 16,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.PromptDesignShuffleButtonPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Shuffle Button Padding',
                defaultValue: {
                  top: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 12,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
            ],
          },
          // [LABELS.TEXT_COLORS_OPTION_KEY]: {
          //   items: [
          //     {
          //       id: ComponentSettingKey.PromptDesignGroupColor,
          //       type: BuilderComponentSettingType.Group,
          //       label: '',
          //       groupItems: {
          //         [LABELS.DIVIDER_KEY]: [
          //           ...makeTextColorSettings(
          //             ComponentSettingKey.PromptDesignDividerColor,
          //             ComponentSettingKey.PromptDesignDividerColorSolid,
          //             ComponentSettingKey.PromptDesignDividerColorStartGradient,
          //             ComponentSettingKey.PromptDesignDividerColorEndGradient,
          //             ComponentSettingKey.PromptDesignDividerColorGradientDirection,
          //             ComponentSettingKey.PromptDesignScheme,
          //             LABELS.DIVIDER_KEY,
          //             'adaptive'
          //           ).items,
          //         ],
          //         [LABELS.PLACEHOLDER_KEY]: [
          //           ...makeTextColorSettings(
          //             ComponentSettingKey.PromptDesignPlaceholderColor,
          //             ComponentSettingKey.PromptDesignPlaceholderColorSolid,
          //             ComponentSettingKey.PromptDesignPlaceholderColorStartGradient,
          //             ComponentSettingKey.PromptDesignPlaceholderColorEndGradient,
          //             ComponentSettingKey.PromptDesignPlaceholderColorGradientDirection,
          //             ComponentSettingKey.PromptDesignScheme,
          //             LABELS.PLACEHOLDER_KEY,
          //             'adaptive'
          //           ).items,
          //         ],
          //       },
          //     },
          //   ],
          // },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.PromptDesignTypography, [
            {
              id: ComponentSettingKey.PromptDesignPlaceholderTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Small',
            },
            {
              id: ComponentSettingKey.PromptDesignSuggestionTypography,
              label: 'Suggestion',
              defaultValue: 'Body Small',
            },
          ]),
          [LABELS.BORDER_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignBorderThickness,
                type: BuilderComponentSettingType.Number,
                label: LABELS.LINE_THICKNESS_KEY,
                defaultValue: 1,
                numberSettings: {
                  min: 0,
                },
              },
              ...makeTextColorSettings(
                ComponentSettingKey.PromptDesignBorderColor,
                ComponentSettingKey.PromptDesignBorderColorSolid,
                undefined,
                undefined,
                undefined,
                ComponentSettingKey.PromptDesignScheme,
                'Border',
                'adaptive',
                {
                  showGradient: false,
                }
              ).items,
            ],
          },
          [LABELS.ELEVATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignElevation,
                label: LABELS.ELEVATION_KEY,
                type: BuilderComponentSettingType.Switch,
                defaultValue: false,
              },
            ],
          },
          [LABELS.CORNER_RADIUS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignCornerRadius,
                type: BuilderComponentSettingType.Select,
                label: LABELS.COMPONENT_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
              {
                id: ComponentSettingKey.PromptDesignButtonCornerRadius,
                type: BuilderComponentSettingType.Select,
                label: LABELS.BUTTON_KEY,
                defaultValue: MdShape.Medium,
                selectSettings: {
                  options: Object.entries(MdShape).map(([label, value]) => ({
                    value,
                    label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
                  })),
                },
              },
            ],
          },
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PromptDesignNumberDefaultItems,
                label: 'Number of Default Prompts',
                type: BuilderComponentSettingType.Number,
                defaultValue: 4,
                numberSettings: {
                  min: 3,
                  max: 10,
                  step: 1,
                },
              },
            ],
          },
          'Hover Effect (Chips)': {
            items: [
              {
                id: ComponentSettingKey.PromptDesignHoverEffectVariant,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.PromptActionOnButtonClick,
            label: 'On Button Click',
            isSimple: false,
          },
          {
            id: ComponentSettingKey.PromptActionOnImproveAiClick,
            label: 'On Improve Click',
            isSimple: false,
          },
        ],
        behavior: {
          visibility: true,
          custom: {
            [LABELS.VALIDATION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PromptBehaviorAcceptedFiles,
                  type: BuilderComponentSettingType.Select,
                  label: LABELS.ACCEPTED_FILES_KEY,
                  defaultValue: ALLOWED_PROMPT_TYPES,
                  selectSettings: {
                    multiple: true,
                    options: ALLOWED_PROMPT_TYPES.map((o) => {
                      return {
                        value: o,
                        label: o,
                      }
                    }),
                  },
                },
                {
                  id: ComponentSettingKey.PromptBehaviorMaxSize,
                  type: BuilderComponentSettingType.NumberWithUnit,
                  label: LABELS.MAX_SIZE_KEY,
                  help: 'Maximum allowed size is 10MB',
                  defaultValue: {
                    value: 10,
                    unit: 'mb',
                  },
                  numberWithUnitSettings: {
                    min: 0,
                    step: 5,
                    options: [
                      {
                        value: 'kb',
                        label: 'KB',
                        max: 1000,
                      },
                      {
                        value: 'mb',
                        label: 'MB',
                        max: 10,
                      },
                    ],
                  },
                },
                {
                  id: ComponentSettingKey.PromptBehaviorMaxFiles,
                  type: BuilderComponentSettingType.Slider,
                  label: LABELS.MAX_FILES_KEY,
                  sliderSettings: {
                    min: 1,
                    max: 10,
                    step: 1,
                  },
                  defaultValue: [10],
                },
              ],
            },
            [LABELS.COLOR_SELECTION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PromptBehaviorColorSelection,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.COLOR_SELECTION_KEY,
                  defaultValue: true,
                },
              ],
            },
            [LABELS.FILE_SELECTION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PromptBehaviorFileSelection,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.FILE_SELECTION_KEY,
                  defaultValue: true,
                },
              ],
            },
            [LABELS.FONT_SELECTION_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PromptBehaviorFontSelection,
                  type: BuilderComponentSettingType.Switch,
                  label: LABELS.FONT_SELECTION_KEY,
                  defaultValue: true,
                },
              ],
            },
          },
        },
      },
    },

    // quantity
    {
      compId: 'quantity',
      title: 'Quantity',
      searchKeywords: ['quantity', 'number input', 'counter', 'stepper', 'plus/minus', 'amount', 'item count'],
      icon: 'quantity',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              // makeDataSource(ComponentSettingKey.QuantityDataSource),
              {
                id: ComponentSettingKey.QuantityDataValue,
                label: LABELS.VALUE_KEY,
                type: BuilderComponentSettingType.VariableSelect,
                variableSelectSettings: {
                  flags: VariableSelectFeatureFlag.SingleCell | VariableSelectFeatureFlag.CurrentRow,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              // {
              //   id: ComponentSettingKey.QuantityDataValue,
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   label: LABELS.VALUE_KEY,
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.QuantityDataSource,
              //     columnTypes: [ColumnTypes.Number],
              //   },
              // },
              {
                id: ComponentSettingKey.QuantityDataMin,
                type: BuilderComponentSettingType.Number,
                label: LABELS.MIN_VALUE_KEY,
                defaultValue: 1,
              },
              {
                id: ComponentSettingKey.QuantityDataMax,
                type: BuilderComponentSettingType.Number,
                label: LABELS.MAX_VALUE_KEY,
                defaultValue: 10,
              },
            ],
          },
          [LABELS.LABEL_KEY]: {
            items: [
              {
                id: ComponentSettingKey.QuantityDataLabel,
                type: BuilderComponentSettingType.VariableText,
                label: 'Label',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
              },
            ],
          },
        },
        designGroup: {
          [LABELS.TEXT_COLORS_OPTION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.QuantityDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.BUTTON_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.QuantityDesignButtonColor,
                      ComponentSettingKey.QuantityDesignButtonColorSolid,
                      ComponentSettingKey.QuantityDesignButtonColorStartGradient,
                      ComponentSettingKey.QuantityDesignButtonColorEndGradient,
                      ComponentSettingKey.QuantityDesignButtonColorGradientDirection,
                      ComponentSettingKey.QuantityDesignScheme,
                      LABELS.BUTTON_KEY,
                      'adaptive'
                    ).items,
                  ],
                  [LABELS.INPUT_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.QuantityDesignInputColor,
                      ComponentSettingKey.QuantityDesignInputColorSolid,
                      undefined,
                      undefined,
                      undefined,
                      ComponentSettingKey.QuantityDesignScheme,
                      LABELS.INPUT_KEY,
                      'adaptive',
                      {
                        showGradient: false,
                      }
                    ).items,
                  ],
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.QuantityDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Margin',
                defaultValue: {
                  top: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 8,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.QuantityDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.QuantityActionOnIncrease,
            label: 'On Increase',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
          {
            id: ComponentSettingKey.QuantityActionOnDecrease,
            label: 'On Decrease',
            isSimple: false,
            defaultValue: [
              {
                type: IActionType.IncrementValue,
                settings: {},
              },
            ],
          },
        ],
      },
    },

    // pricing
    {
      compId: 'pricing',
      title: 'Pricing',
      searchKeywords: ['pricing', 'pricing table', 'plans', 'cost', 'subscription', 'packages', 'fees', 'tiers'],
      icon: 'pricing',
      nested: false,
      settings: {
        dataGroup: {
          Plans: {
            items: [
              {
                id: ComponentSettingKey.PricingDataDownloadLink,
                type: BuilderComponentSettingType.DownloadLink,
                label: '',
                downloadLinkSettings: {
                  text: 'Download this CSV for plans and features to get started',
                  link: 'https://docs.google.com/spreadsheets/d/1yhHWIOMxRlN9CV0gSMs_LEsvCH9WMd1AMC1XGP7eZoY/edit?usp=sharing',
                  openInNewTab: true,
                },
              },
              makeDataSource(ComponentSettingKey.PricingDataSource),
              {
                id: ComponentSettingKey.PricingDataName,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Plan',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.Template],
                },
              },
              // {
              //   id: ComponentSettingKey.PricingDataDescription,
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   label: 'Description',
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.PricingDataSource,
              //     columnTypes: [ColumnTypes.Text],
              //   },
              // },
              {
                id: ComponentSettingKey.PricingDataMonthlyPrice,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Monthly Price',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              {
                id: ComponentSettingKey.PricingDataYearlyPrice,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Yearly Price',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              {
                id: ComponentSettingKey.PricingDataYearlyDiscount,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Yearly Discount',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Number],
                },
              },
              {
                id: ComponentSettingKey.PricingDataBadgeText,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Status Badge',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Text],
                },
              },
              {
                id: ComponentSettingKey.PricingDataButtonText,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Button Label',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Text, ColumnTypes.Template],
                },
              },
              {
                id: ComponentSettingKey.PricingDataPopular,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Popular',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
              {
                id: ComponentSettingKey.PricingDataDefault,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Default',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PricingDataSource,
                  columnTypes: [ColumnTypes.Boolean],
                },
              },
              // {
              //   id: ComponentSettingKey.PricingDataFeatureLabel,
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   label: 'Feature Title',
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.PricingDataSource,
              //     columnTypes: [ColumnTypes.Text],
              //   },
              // },
              // {
              //   id: ComponentSettingKey.PricingDataFeatureValue,
              //   type: BuilderComponentSettingType.ColumnSelect,
              //   label: 'Feature Details',
              //   columnSelectSettings: {
              //     tableSourceId: ComponentSettingKey.PricingDataSource,
              //     columnTypes: [ColumnTypes.Text],
              //   },
              // },
            ],
          },
          Features: {
            items: [
              {
                id: ComponentSettingKey.PricingDataFeaturesSource,
                type: BuilderComponentSettingType.DataTableColumnList,
                label: 'Feature Selection',
                selectIcon: true,
                showDeviceVisibility: false,
                tableColumnListSettings: {
                  showModal: true,
                },
              },
            ],
          },
          // 'Additional Settings': {
          //   items: [
          //     {
          //       id: ComponentSettingKey.PricingDataYearlyDiscount,
          //       type: BuilderComponentSettingType.VariableText,
          //       label: 'Yearly Discount',
          //       variableTextSettings: {
          //         flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon,
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDataMonthlyDiscount,
          //       type: BuilderComponentSettingType.VariableText,
          //       label: 'Monthly Discount',
          //       variableTextSettings: {
          //         flags: VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon,
          //       },
          //     },
          //   ],
          // },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.ButtonDesignGroupColor,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  Borders: [
                    {
                      id: ComponentSettingKey.PricingDesignCardBorder,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Card Border',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignBorder,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Table',
                      defaultValue: 'primary-70',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignHeaderBorder,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Dividers',
                      defaultValue: 'primary-80',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignAccentColor,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Selected Plan',
                      defaultValue: 'primary-60',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                  ],
                  'Popular Badge': [
                    {
                      id: ComponentSettingKey.PricingDesignCardPopularBorder,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Card Popular Border',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignCardPopularRing,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Card Popular Ring',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignCardBadgeBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Card Badge Background',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignPopularBadgeBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Background',
                      defaultValue: 'extended-1',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignPopularBadgeText,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Label',
                      defaultValue: 'primary-0',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                  ],
                  Icons: [
                    {
                      id: ComponentSettingKey.PricingDesignIconSuccess,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'True',
                      defaultValue: 'extended-6',
                      // dependOn: {
                      //   id: ComponentSettingKey.PricingDesignLayout,
                      //   showWhen: (value) => value === 'table' || value === 'hybrid',
                      // },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignIconError,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'False',
                      defaultValue: 'extended-7',
                      // dependOn: {
                      //   id: ComponentSettingKey.PricingDesignLayout,
                      //   showWhen: (value) => value === 'table' || value === 'hybrid',
                      // },
                    },
                  ],
                  Buttons: [
                    {
                      id: ComponentSettingKey.PricingDesignCardButtonPrimary,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Selected Plan',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignCardButtonSecondary,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Unselected Plan',
                      defaultValue: 'primary-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'cards',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignButtonPrimary,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Selected Plan',
                      defaultValue: 'extended-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignButtonSecondary,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Unselected Plan',
                      defaultValue: 'extended-10',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                  ],
                  Toggle: [
                    {
                      id: ComponentSettingKey.PricingDesignBillingToggleBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Not selected',
                      defaultValue: 'primary-80',
                      // dependOn: {
                      //   id: ComponentSettingKey.PricingDesignLayout,
                      //   showWhen: (value) => value === 'table' || value === 'hybrid',
                      // },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignBillingToggleActiveBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Selected',
                      defaultValue: 'primary-30',
                      // dependOn: {
                      //   id: ComponentSettingKey.PricingDesignLayout,
                      //   showWhen: (value) => value === 'table' || value === 'hybrid',
                      // },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignBillingToggleDiscountBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Discount',
                      defaultValue: 'extended-7',
                      // dependOn: {
                      //   id: ComponentSettingKey.PricingDesignLayout,
                      //   showWhen: (value) => value === 'table' || value === 'hybrid',
                      // },
                    },
                  ],
                  Background: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.PricingDesignCardBackgroundType,
                      ComponentSettingKey.PricingDesignCardBackgroundSolid,
                      ComponentSettingKey.PricingDesignCardBackgroundStartGradient,
                      ComponentSettingKey.PricingDesignCardBackgroundEndGradient,
                      ComponentSettingKey.PricingDesignCardBackgroundGradientDirection,
                      ComponentSettingKey.PricingDesignScheme,
                      'Background',
                      'solid',
                      { showAdaptive: false }
                    ).items,
                    {
                      id: ComponentSettingKey.PricingDesignHeaderBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Header & Footer',
                      defaultValue: 'primary-90',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignEvenRowBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Even Row',
                      defaultValue: 'primary-90',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignOddRowBackground,
                      type: BuilderComponentSettingType.ColorVariant,
                      label: 'Odd Row',
                      defaultValue: 'primary-80',
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                    {
                      id: ComponentSettingKey.PricingDesignColumnBackgroundOpacity,
                      type: BuilderComponentSettingType.Slider,
                      label: '',
                      defaultValue: [10],
                      sliderSettings: {
                        min: 0,
                        max: 100,
                        step: 10,
                      },
                      dependOn: {
                        id: ComponentSettingKey.PricingDesignLayout,
                        showWhen: (value) => value === 'table' || value === 'hybrid',
                      },
                    },
                  ],
                },
              },
            ],
          },
          // [LABELS.COLORS_KEY]: {
          //   items: [
          //     // 🎨 BASIC COLORS
          //     {
          //       id: ComponentSettingKey.PricingDesignBorder,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Table',
          //       defaultValue: 'primary-90',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignHeaderBorder,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Dividers',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignAccentColor,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Selected Plan',
          //       defaultValue: 'primary-80',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },

          //     // 📝 TEXT COLORS
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignTextPrimary,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Text Primary',
          //     //   defaultValue: 'primary-80',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignTextSecondary,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Text Secondary',
          //     //   defaultValue: 'primary-80',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignHeaderText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Header Text',
          //     //   defaultValue: 'primary-90',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignPriceText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Price Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },

          //     // 🏷️ BADGE & STATUS
          //     {
          //       id: ComponentSettingKey.PricingDesignPopularBadgeBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Background',
          //       defaultValue: 'primary-50',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignPopularBadgeText,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Label',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignIconSuccess,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'True',
          //       defaultValue: 'primary-80',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignIconError,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'False',
          //       defaultValue: 'secondary-50',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },

          //     // 🔘 BUTTON COLORS
          //     {
          //       id: ComponentSettingKey.PricingDesignButtonPrimary,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Selected Plan',
          //       defaultValue: 'secondary-30',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignButtonPrimaryText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Button Primary Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     {
          //       id: ComponentSettingKey.PricingDesignButtonSecondary,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Unselected Plan',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignButtonSecondaryText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Button Secondary Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignButtonDisabled,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Button Disabled',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignButtonDisabledText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Button Disabled Text',
          //     //   defaultValue: 'neutral-70',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },

          //     // 🔄 BILLING TOGGLE
          //     {
          //       id: ComponentSettingKey.PricingDesignBillingToggleBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Not selected',
          //       defaultValue: 'primary-20',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignBillingToggleActiveBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Selected',
          //       defaultValue: 'primary-30',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignBillingToggleActiveText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Billing Toggle Active Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignBillingToggleInactiveText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Billing Toggle Inactive Text',
          //     //   defaultValue: 'primary-90',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignBillingToggleInactiveHoverText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Billing Toggle Inactive Hover Text',
          //     //   defaultValue: 'primary-80',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },
          //     {
          //       id: ComponentSettingKey.PricingDesignBillingToggleDiscountBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Discount',
          //       defaultValue: 'primary-50',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignBillingToggleDiscountText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Billing Toggle Discount Text',
          //     //   defaultValue: 'secondary-50',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },

          //     // 📊 TABLE ROWS
          //     {
          //       id: ComponentSettingKey.PricingDesignHeaderBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Header & Footer',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignEvenRowBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Even Row',
          //       defaultValue: 'primary-20',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignOddRowBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Odd Row',
          //       defaultValue: 'primary-30',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignFooterBackground,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Footer Background',
          //     //   defaultValue: 'primary-20',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'table' || value === 'hybrid',
          //     //   },
          //     // },

          //     // Color for Card
          //     {
          //       id: ComponentSettingKey.PricingDesignCardBorder,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Border',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignCardPopularBorder,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Popular Border',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignCardPopularRing,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Popular Ring',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardTitleText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Title Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardPriceText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Price Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     {
          //       id: ComponentSettingKey.PricingDesignCardBadgeBackground,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Badge Background',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     {
          //       id: ComponentSettingKey.PricingDesignCardButtonPrimary,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Button Primary',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardButtonPrimaryText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Button Primary Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     {
          //       id: ComponentSettingKey.PricingDesignCardButtonSecondary,
          //       type: BuilderComponentSettingType.ColorVariant,
          //       label: 'Card Button Secondary',
          //       defaultValue: 'primary-10',
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'cards',
          //       },
          //     },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardButtonSecondaryText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Button Secondary Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardButtonDisabled,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Button Disabled',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     // {
          //     //   id: ComponentSettingKey.PricingDesignCardButtonDisabledText,
          //     //   type: BuilderComponentSettingType.ColorVariant,
          //     //   label: 'Card Button Disabled Text',
          //     //   defaultValue: 'primary-95',
          //     //   dependOn: {
          //     //     id: ComponentSettingKey.PricingDesignLayout,
          //     //     showWhen: (value) => value === 'cards',
          //     //   },
          //     // },
          //     ...makeTextColorSettings(
          //       ComponentSettingKey.PricingDesignCardBackgroundType,
          //       ComponentSettingKey.PricingDesignCardBackgroundSolid,
          //       ComponentSettingKey.PricingDesignCardBackgroundStartGradient,
          //       ComponentSettingKey.PricingDesignCardBackgroundEndGradient,
          //       ComponentSettingKey.PricingDesignCardBackgroundGradientDirection,
          //       ComponentSettingKey.PricingDesignScheme,
          //       'Background',
          //       'solid'
          //     ).items,

          //     // ️ OPACITY SETTINGS
          //     {
          //       id: ComponentSettingKey.PricingDesignColumnBackgroundOpacity,
          //       type: BuilderComponentSettingType.Slider,
          //       label: 'Column Background Opacity',
          //       defaultValue: 0,
          //       sliderSettings: {
          //         min: 0,
          //         max: 100,
          //         step: 10,
          //       },
          //       dependOn: {
          //         id: ComponentSettingKey.PricingDesignLayout,
          //         showWhen: (value) => value === 'table' || value === 'hybrid',
          //       },
          //     },
          //   ],
          // },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PricingDesignLayoutAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Layout Alignment',
                defaultValue: 'center',
              },
              {
                id: ComponentSettingKey.PricingDesignBillingToggleAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Billing Toggle Alignment',
                defaultValue: 'center',
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PricingDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
                defaultValue: {
                  top: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 24,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.PricingDesignGap,
                type: BuilderComponentSettingType.Select,
                label: 'Element Spacing',
                defaultValue: 'large',
                selectSettings: {
                  options: [
                    // 'none' | 'small' | 'medium' | 'large' | 'xlarge'
                    { value: 'none', label: 'None' },
                    { value: 'small', label: 'Small' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'large', label: 'Large' },
                    { value: 'xlarge', label: 'Extra Large' },
                  ],
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              ...makeClampWidthItems(ComponentSettingKey.PricingDesignWidth, {
                defaultValue: '100%' as any,
              }).items,
            ],
          },
          [LABELS.LAYOUT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PricingDesignLayout,
                type: BuilderComponentSettingType.Select,
                label: 'Layout',
                defaultValue: 'default',
                selectSettings: {
                  options: [
                    { value: 'table', label: 'Table' },
                    { value: 'cards', label: 'Cards' },
                  ],
                },
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PricingDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.PricingActionOnClickSelectPlan,
            label: 'On Click Select Plan',
            isSimple: false,
            defaultValue: [],
            actionTypes: [IActionType.ShowSnackbar],
          },
        ],
        behavior: {
          visibility: true,
          custom: {
            [LABELS.SHOW_HIDE_KEY]: {
              items: [
                {
                  id: ComponentSettingKey.PricingDesignShowBillingToggle,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show Billing',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.PricingDesignShowBadges,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show Badges',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.PricingDesignShowPopular,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show Popular',
                  defaultValue: true,
                },
                {
                  id: ComponentSettingKey.PricingDesignShowButton,
                  type: BuilderComponentSettingType.Switch,
                  label: 'Show Button',
                  defaultValue: true,
                },
              ],
            },
            'Default Billing Cycle': {
              items: [
                {
                  id: ComponentSettingKey.PricingDesignBillingCycle,
                  type: BuilderComponentSettingType.Select,
                  label: 'Billing Cycle',
                  defaultValue: 'monthly',
                  selectSettings: {
                    options: [
                      { value: 'monthly', label: 'Month' },
                      { value: 'yearly', label: 'Year' },
                    ],
                  },
                },
              ],
            },
          },
        },
      },
    },

    // Newsletter
    {
      compId: 'newsletter',
      title: 'Newsletter',
      searchKeywords: ['newsletter', 'email signup', 'subscribe', 'mailing list', 'subscription form'],
      icon: 'newsletter',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.NewsletterDataSource),
              {
                id: ComponentSettingKey.NewsletterDataEmail,
                type: BuilderComponentSettingType.ColumnSelect,
                label: 'Email',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.NewsletterDataSource,
                  columnTypes: [ColumnTypes.Email],
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDataCompTitle,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon | VariableTextFeatureFlag.SessionID,
                },
                label: 'Title',
              },
            ],
          },
          [LABELS.LABELS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDataPlaceholder,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon | VariableTextFeatureFlag.SessionID,
                },
                label: 'Placeholder',
                defaultValue: [{ type: 'paragraph', children: [{ text: 'Enter your email' }] }],
              },
              {
                id: ComponentSettingKey.NewsletterDataButtonText,
                type: BuilderComponentSettingType.VariableText,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon | VariableTextFeatureFlag.SessionID,
                },
                label: 'Button Label',
                defaultValue: [{ type: 'paragraph', children: [{ text: 'Subscribe' }] }],
              },
            ],
          },
          [LABELS.ICON_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDataIcon,
                type: BuilderComponentSettingType.Icon,
                label: 'Icon',
                defaultValue: 'mail',
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDesignColorGroup,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.BUTTON_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.NewsletterDesignButtonColor,
                      ComponentSettingKey.NewsletterDesignButtonColorSolid,
                      ComponentSettingKey.NewsletterDesignButtonColorStartGradient,
                      ComponentSettingKey.NewsletterDesignButtonColorEndGradient,
                      ComponentSettingKey.NewsletterDesignButtonColorGradientDirection,
                      ComponentSettingKey.NewsletterDesignColorScheme,
                      LABELS.BUTTON_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
              {
                id: ComponentSettingKey.NewsletterDesignCompTitleColorGroup,
                type: BuilderComponentSettingType.Group,
                label: '',
                groupItems: {
                  [LABELS.COMPONENT_TITLE_KEY]: [
                    ...makeTextColorSettings(
                      ComponentSettingKey.NewsletterDesignCompTitleColor,
                      ComponentSettingKey.NewsletterDesignCompTitleColorSolid,
                      ComponentSettingKey.NewsletterDesignCompTitleColorStartGradient,
                      ComponentSettingKey.NewsletterDesignCompTitleColorEndGradient,
                      ComponentSettingKey.NewsletterDesignCompTitleColorGradientDirection,
                      ComponentSettingKey.NewsletterDesignColorScheme,
                      LABELS.COMPONENT_TITLE_KEY,
                      'adaptive'
                    ).items,
                  ],
                },
              },
              {
                id: ComponentSettingKey.NewsletterDesignBorderColor,
                type: BuilderComponentSettingType.ColorVariant,
                label: 'Border',
                defaultValue: 'primary-90',
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDesignSize,
                type: BuilderComponentSettingType.Select,
                label: 'Size',
                defaultValue: 'md',
                selectSettings: {
                  options: [
                    { value: 'sm', label: 'Small' },
                    { value: 'md', label: 'Medium' },
                    { value: 'lg', label: 'Large' },
                  ],
                },
              },
              {
                id: ComponentSettingKey.NewsletterDesignIconWidth,
                type: BuilderComponentSettingType.Slider,
                label: 'Icon',
                defaultValue: [24],
                sliderSettings: {
                  max: 100,
                  min: 18,
                  range: false,
                  step: 1,
                },
              },
              ...makeClampWidthItems(ComponentSettingKey.NewsletterDesignWidth).items,
            ],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDesignAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Component Alignment',
                defaultValue: 'center',
              },
              {
                id: ComponentSettingKey.NewsletterDesignCompTitleAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: 'Title Alignment',
                defaultValue: 'center',
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
                defaultValue: {
                  top: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.NewsletterDesignCompTitleMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Title Margin',
                defaultValue: {
                  top: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.NewsletterDesignPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Padding',
                defaultValue: {
                  top: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.NewsletterDesignTypography, [
            {
              id: ComponentSettingKey.NewsletterDesignComponentTitleTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.NewsletterDesignInputTypography,
              label: LABELS.EMAIL_KEY,
              defaultValue: 'Body Medium',
            },
            {
              id: ComponentSettingKey.NewsletterDesignButtonTypography,
              label: LABELS.BUTTON_KEY,
              defaultValue: 'Button Medium',
            },
          ]),
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.NewsletterDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        action: [
          {
            id: ComponentSettingKey.NewsletterActionOnClickSubscribe,
            label: 'On Click Subscribe',
            isSimple: false,
            defaultValue: [],
          },
        ],
        behavior: {
          visibility: true,
        },
      },
    },

    // Alert
    {
      compId: 'alert',
      title: 'Alert',
      searchKeywords: ['alert', 'notification', 'message', 'toast'],
      icon: 'alert',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDataTitle,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.TITLE_KEY,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon | VariableTextFeatureFlag.SessionID,
                },
                defaultValue: [{ type: 'paragraph', children: [{ text: 'Title' }] }],
              },
            ],
          },
          [LABELS.SUB_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDataDescription,
                type: BuilderComponentSettingType.VariableText,
                label: LABELS.SUB_TITLE_KEY,
                variableTextSettings: {
                  flags:
                    VARIABLE_TEXT_FEATURE_FLAG_TEXT | VariableTextFeatureFlag.Icon | VariableTextFeatureFlag.SessionID,
                },
                defaultValue: [{ type: 'paragraph', children: [{ text: 'Sub Title' }] }],
              },
            ],
          },
          [LABELS.ICON_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDataIcon,
                type: BuilderComponentSettingType.Icon,
                label: LABELS.ICON_KEY,
                defaultValue: 'add_alert',
              },
            ],
          },
        },
        designGroup: {
          [LABELS.COLORS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignColorVariant,
                type: BuilderComponentSettingType.ColorVariant,
                label: LABELS.BACKGROUND_KEY,
                defaultValue: 'primary-30',
                colorVariantSettings: { schemeId: ComponentSettingKey.AlertDesignScheme, fullColor: 'bg' },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: {
            items: [...makeClampWidthItems(ComponentSettingKey.AlertDesignWidth).items],
          },
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.ALIGNMENT_KEY,
                defaultValue: 'center',
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.AlertDesignTypography, [
            {
              id: ComponentSettingKey.AlertDesignTitleTypography,
              label: LABELS.TITLE_KEY,
              defaultValue: 'Title Medium',
            },
            {
              id: ComponentSettingKey.AlertDesignDescriptionTypography,
              label: LABELS.DESCRIPTION_KEY,
              defaultValue: 'Body Medium',
            },
          ]),
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignMargin,
                type: BuilderComponentSettingType.Spacing,
                label: 'Component Margin',
                defaultValue: {
                  top: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
              },
              {
                id: ComponentSettingKey.AlertDesignPadding,
                type: BuilderComponentSettingType.Spacing,
                label: 'Padding',
              },
            ],
          },
          [LABELS.OUTLINE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignOutline,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.OUTLINE_KEY,
                defaultValue: false,
              },
            ],
          },
          [LABELS.ELEVATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignElevation,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.ELEVATION_KEY,
                defaultValue: false,
              },
            ],
          },
          [LABELS.HOVER_EFFECT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.AlertDesignHoverEffect,
                type: BuilderComponentSettingType.Select,
                label: LABELS.HOVER_EFFECT_KEY,
                selectSettings: {
                  options: hoverEffectOptions,
                },
              },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },
  ],
}
